import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import {
  ErrorApi,
  errorApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  AccountEntity,
  CloudResourceEntity,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import { useAccounts } from '@internal/backstage-plugin-devops-react';
import {
  AccountCloudResourcesMenu,
  cloudResourcesMenuApiRef,
} from '../../api';

interface CloudResourcesContextProviderProps {
  children: ReactNode;
  pageType: PageType;
  entityName: string;
}

export enum PageType {
  PROJECT = 'domain',
  ACCOUNT = 'account',
  RULE = 'rule',
}

/** Dimensions that can be counted against the other active filters. */
export type FacetDimension =
  | 'category'
  | 'service'
  | 'type'
  | 'region'
  | 'account';

type FacetField =
  | 'category'
  | 'service'
  | 'type'
  | 'region'
  | 'account';

const FACET_FIELD: Record<FacetDimension, FacetField> = {
  category: 'category',
  service: 'service',
  type: 'type',
  region: 'region',
  account: 'account',
};

export type ResourceFailedRule = {
  ruleName: string;
  severity: string;
};

const matchesSearch = (
  resource: CloudResourceEntity,
  search: string,
): boolean => {
  if (!search) return true;
  const needle = search.toLowerCase();
  return [
    resource.metadata.title,
    resource.metadata.name,
    resource.metadata.description,
    resource.spec.type,
    resource.spec.service,
    resource.spec.category,
    resource.spec.owner,
  ].some(value => value?.toLowerCase().includes(needle));
};

enum ActionTypes {
  SELECT_GROUP_BY = 'SELECT_GROUP_BY',
  SELECT_REGIONS = 'SELECT_REGIONS',
  SELECT_ACCOUNTS = 'SELECT_ACCOUNTS',
  SELECT_TYPE = 'SELECT_TYPE',
  UNSELECT_TYPE = 'UNSELECT_TYPE',
  SELECT_SERVICE = 'SELECT_SERVICE',
  UNSELECT_SERVICE = 'UNSELECT_SERVICE',
  SET_SEARCH = 'SET_SEARCH',
  TOGGLE_CATEGORY = 'TOGGLE_CATEGORY',
  SET_CATEGORIES = 'SET_CATEGORIES',
  FILTER_COMPLETED = 'FILTER_COMPLETED',
  LOAD_COMPLETED = 'LOAD_COMPLETED',
  MENU_COMPLETED = 'MENU_COMPLETED',
  RULES_COMPLETED = 'RULES_COMPLETED',
  SELECT_RESOURCE = 'SELECT_RESOURCE',
  UNSELECT_RESOURCE = 'UNSELECT_RESOURCE',
}

type SelectGroupByAction = {
  type: ActionTypes.SELECT_GROUP_BY;
  payload: string[];
};

type SelectRegionsAction = {
  type: ActionTypes.SELECT_REGIONS;
  payload: string[];
};

type SelectAccountsAction = {
  type: ActionTypes.SELECT_ACCOUNTS;
  payload: string[];
};

type SelectTypeAction = {
  type: ActionTypes.SELECT_TYPE;
  payload: string;
};

type UnselectTypeAction = {
  type: ActionTypes.UNSELECT_TYPE;
  payload: string;
};

type SelectServiceAction = {
  type: ActionTypes.SELECT_SERVICE;
  payload: string;
};

type UnselectServiceAction = {
  type: ActionTypes.UNSELECT_SERVICE;
  payload: string;
};

type SetSearchAction = {
  type: ActionTypes.SET_SEARCH;
  payload: string;
};

type ToggleCategoryAction = {
  type: ActionTypes.TOGGLE_CATEGORY;
  payload: string;
};

type SetCategoriesAction = {
  type: ActionTypes.SET_CATEGORIES;
  payload: string[];
};

type FilterCompletedAction = {
  type: ActionTypes.FILTER_COMPLETED;
  payload: {
    filteredResources: CloudResourceEntity[];
    resourceExample: CloudResourceEntity | undefined;
  };
};

type LoadCompletedAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: CloudResourceEntity[];
};

type MenuCompletedAction = {
  type: ActionTypes.MENU_COMPLETED;
  payload: Record<string, TypeInfo[]>;
};

type RulesCompletedAction = {
  type: ActionTypes.RULES_COMPLETED;
  payload: RuleEntity[];
};

type SelectResourceAction = {
  type: ActionTypes.SELECT_RESOURCE;
  payload: CloudResourceEntity;
};

type UnselectResourceAction = {
  type: ActionTypes.UNSELECT_RESOURCE;
};
type CloudResourcesAction =
  | LoadCompletedAction
  | MenuCompletedAction
  | RulesCompletedAction
  | SelectGroupByAction
  | SelectRegionsAction
  | SelectAccountsAction
  | SelectTypeAction
  | UnselectTypeAction
  | SelectServiceAction
  | UnselectServiceAction
  | SetSearchAction
  | ToggleCategoryAction
  | SetCategoriesAction
  | FilterCompletedAction
  | SelectResourceAction
  | UnselectResourceAction;

type CloudResourcesState = {
  allCloudResources: CloudResourceEntity[];
  cloudResources: CloudResourceEntity[];
  resourceExample: CloudResourceEntity | undefined;
  selectedResource: CloudResourceEntity | undefined;
  allGroupBy: string[];
  selectedGroupBy: string[];
  allRegions: string[];
  selectedRegions: string[];
  allAccounts: string[];
  selectedAccounts: string[];
  allCategoriesWithServices: Record<string, string[]>;
  allServicesWithTypes: Record<string, TypeInfo[]>;
  menuServicesWithTypes: Record<string, TypeInfo[]>;
  selectedTypes: string[];
  selectedCategories: string[];
  search: string;
  rules: RuleEntity[];
  hasRegionFilter: boolean;
  hasTypesFilter: boolean;
  hasAccountFilter: boolean;
  status: string;
};

type TypeInfo = {
  name: string;
  count: number;
};

// The backend menu covers the union of services across all accounts, so on
// account pages a service may exist only there, missing from the account's
// own entities.
const getTypesOfService = (
  state: CloudResourcesState,
  service: string,
): string[] =>
  (state.menuServicesWithTypes[service] ??
    state.allServicesWithTypes[service] ??
    []).map(typeInfo => typeInfo.name);

type CloudResourcesContextType = {
  cloudResources: CloudResourceEntity[];
  resourceExample: CloudResourceEntity | undefined;
  selectedResource: CloudResourceEntity | undefined;
  allGroupBy: string[];
  selectedGroupBy: string[];
  allRegions: string[];
  allAccounts: string[];
  selectedAccounts: string[];
  /** account name -> CloudAccountType (AWS/Azure), for provider badges */
  accountProvider: Record<string, string>;
  selectedRegions: string[];
  allCategoriesWithServices: Record<string, string[]>;
  allServicesWithTypes: Record<string, TypeInfo[]>;
  menuServicesWithTypes: Record<string, TypeInfo[]>;
  selectedTypes: string[];
  selectedCategories: string[];
  search: string;
  rules: RuleEntity[];
  failedRulesByResource: Record<string, ResourceFailedRule[]>;
  hasRegionFilter: boolean;
  hasTypesFilter: boolean;
  hasAccountFilter: boolean;
  setSelectedGroupBy: (groupBy: string[]) => void;
  setSelectedRegions: (regions: string[]) => void;
  setSelectedAccounts: (accounts: string[]) => void;
  selectType: (type: string) => void;
  unselectType: (type: string) => void;
  selectService: (service: string) => void;
  unselectService: (service: string) => void;
  setSearch: (search: string) => void;
  toggleCategory: (category: string) => void;
  setSelectedCategories: (categories: string[]) => void;
  facetResources: (dimension: FacetDimension) => CloudResourceEntity[];
  facetCounts: (dimension: FacetDimension) => Record<string, number>;
  selectResource: (resource: CloudResourceEntity) => void;
  unselectResource: () => void;
};

const initialCloudResourcesState: CloudResourcesState = {
  allCloudResources: [],
  cloudResources: [],
  resourceExample: undefined,
  selectedResource: undefined,
  allRegions: [],
  selectedRegions: [],
  allAccounts: [],
  selectedAccounts: [],
  allCategoriesWithServices: {},
  allServicesWithTypes: {},
  menuServicesWithTypes: {},
  selectedTypes: [],
  selectedCategories: [],
  search: '',
  rules: [],
  allGroupBy: ['Flat', 'Category', 'Service', 'Type', 'Region'],
  selectedGroupBy: [],
  hasRegionFilter: true,
  hasTypesFilter: true,
  hasAccountFilter: true,
  status: 'initial_load',
};

export const CloudResourcesContext =
  createContext<CloudResourcesContextType | null>(null);

export function CloudResourcesContextProvider(
  props: Readonly<CloudResourcesContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const cloudResourcesMenuApi = useApi(cloudResourcesMenuApiRef);
  const errorApi = useApi<ErrorApi>(errorApiRef);

  const cloudResourcesReducer = (
    state: CloudResourcesState,
    action: CloudResourcesAction,
  ): CloudResourcesState => {
    switch (action.type) {
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.allCloudResources = action.payload;
        stateCopy.cloudResources = action.payload;
        stateCopy.allRegions = action.payload
          .map(resource => resource.spec.region)
          .filter(
            (value, index, current_value) =>
              current_value.indexOf(value) === index,
          );
        stateCopy.allAccounts = action.payload
          .map(resource => resource.spec.account)
          .filter(
            (value, index, current_value) =>
              current_value.indexOf(value) === index,
          );

        const categoriesWithServicesTemp: Record<string, string[]> = {};
        const servicesWithTypesTemp: Record<string, TypeInfo[]> = {};

        let count: number = 0;

        for (const resource of action.payload) {
          const category = resource.spec.category;
          if (!categoriesWithServicesTemp[category]) {
            categoriesWithServicesTemp[category] = [];
          }

          const service = resource.spec.service;
          if (!categoriesWithServicesTemp[category].includes(service)) {
            categoriesWithServicesTemp[category].push(service);
          }
          if (!servicesWithTypesTemp[service]) {
            servicesWithTypesTemp[service] = [];
          }

          const type = resource.spec.type;
          if (
            !servicesWithTypesTemp[service].some(
              typeInfo => typeInfo.name === type,
            )
          ) {
            servicesWithTypesTemp[service].push({
              name: type,
              count: 1,
            });
            count += 1;
          } else {
            const indexOfType = servicesWithTypesTemp[service].findIndex(
              typeInfo => typeInfo.name === type,
            );
            servicesWithTypesTemp[service][indexOfType].count += 1;
          }
        }
        stateCopy.allCategoriesWithServices = categoriesWithServicesTemp;
        stateCopy.allServicesWithTypes = servicesWithTypesTemp;
        stateCopy.hasRegionFilter = stateCopy.allRegions.length > 1;
        stateCopy.hasTypesFilter = count > 1;
        stateCopy.hasAccountFilter = props.pageType === PageType.PROJECT;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.MENU_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.menuServicesWithTypes = action.payload;
        return stateCopy;
      }
      case ActionTypes.RULES_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.rules = action.payload;
        return stateCopy;
      }
      case ActionTypes.SELECT_GROUP_BY: {
        const stateCopy = { ...state };
        stateCopy.selectedGroupBy = action.payload;
        return stateCopy;
      }
      case ActionTypes.SELECT_REGIONS: {
        const stateCopy = { ...state };
        stateCopy.selectedRegions = action.payload;
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.SELECT_ACCOUNTS: {
        const stateCopy = { ...state };
        stateCopy.selectedAccounts = action.payload;
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.SELECT_TYPE: {
        const stateCopy = { ...state };
        stateCopy.selectedTypes = [...state.selectedTypes, action.payload];
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.UNSELECT_TYPE: {
        const stateCopy = { ...state };
        stateCopy.selectedTypes = state.selectedTypes.filter(
          type => type !== action.payload,
        );
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.SELECT_SERVICE: {
        const stateCopy = { ...state };
        stateCopy.selectedTypes = [
          ...state.selectedTypes,
          ...getTypesOfService(state, action.payload),
        ];
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.UNSELECT_SERVICE: {
        const stateCopy = { ...state };
        const serviceTypes = getTypesOfService(state, action.payload);
        stateCopy.selectedTypes = state.selectedTypes.filter(
          type => !serviceTypes.includes(type),
        );
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.SET_SEARCH: {
        const stateCopy = { ...state };
        stateCopy.search = action.payload;
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.TOGGLE_CATEGORY: {
        const stateCopy = { ...state };
        stateCopy.selectedCategories = state.selectedCategories.includes(
          action.payload,
        )
          ? state.selectedCategories.filter(
              category => category !== action.payload,
            )
          : [...state.selectedCategories, action.payload];
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.SET_CATEGORIES: {
        const stateCopy = { ...state };
        stateCopy.selectedCategories = action.payload;
        stateCopy.status = 'filter';
        return stateCopy;
      }
      case ActionTypes.FILTER_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.cloudResources = action.payload.filteredResources;
        stateCopy.resourceExample = action.payload.resourceExample;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.SELECT_RESOURCE: {
        const stateCopy = { ...state };
        stateCopy.selectedResource = action.payload;
        return stateCopy;
      }
      case ActionTypes.UNSELECT_RESOURCE: {
        const stateCopy = { ...state };
        stateCopy.selectedResource = undefined;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(
    cloudResourcesReducer,
    props.pageType,
    pageType =>
      pageType === PageType.PROJECT
        ? {
            ...initialCloudResourcesState,
            // Project resources gain the account (and provider) dimension,
            // grouped by account by default per the mockup.
            allGroupBy: [
              'Flat',
              'Account',
              'Provider',
              'Category',
              'Service',
              'Type',
              'Region',
            ],
            selectedGroupBy: ['Account'],
          }
        : initialCloudResourcesState,
  );

  const { accounts } = useAccounts();

  const filterResourcesByRelation = (
    resources: CloudResourceEntity[],
  ): CloudResourceEntity[] => {
    if (props.pageType === PageType.RULE) {
      return resources.filter(resource =>
        resource.relations!.find(
          relation =>
            relation.type === 'affectedBy' &&
            relation.targetRef === `rule:default/${props.entityName}`,
        ),
      );
    } else if (props.pageType === PageType.PROJECT) {
      const projectAccounts = accounts
        .filter(account => account.spec!.domain === props.entityName)
        .map(account => account.metadata.name);
      return resources.filter(resource =>
        projectAccounts.includes(resource.spec!.account),
      );
    }

    return resources;
  };

  useEffect(() => {
    const cloudResourceByAccountFilter = {
      kind: 'cloudresource',
      'spec.account': props.entityName,
    };
    const cloudResourceFilter = {
      kind: 'cloudresource,',
    };

    let cancelled = false;
    catalogApi
      .getEntities({
        filter:
          props.pageType === PageType.ACCOUNT
            ? cloudResourceByAccountFilter
            : cloudResourceFilter,
      })
      .then(value => {
        if (cancelled) {
          return;
        }
        const resources = filterResourcesByRelation(
          value.items as CloudResourceEntity[],
        );
        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: resources,
        });
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        // A rejected catalog request must still leave the terminal state,
        // otherwise the tab spins forever with no error message.
        errorApi.post(error as Error);
        dispatch({ type: ActionTypes.LOAD_COMPLETED, payload: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, errorApi, accounts, props.pageType, props.entityName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.pageType !== PageType.ACCOUNT) {
      return undefined;
    }

    let cancelled = false;
    cloudResourcesMenuApi
      .getAccountCloudResourcesMenu(props.entityName)
      .then(response => {
        if (cancelled || !response.succeeded) {
          return;
        }

        const menu = response.body?.item as AccountCloudResourcesMenu;
        const servicesWithTypes: Record<string, TypeInfo[]> = {};
        for (const serviceMenu of menu?.services ?? []) {
          servicesWithTypes[serviceMenu.service] = serviceMenu.types.map(
            typeStatistic => ({
              name: typeStatistic.type,
              count: typeStatistic.resourcesCount,
            }),
          );
        }
        dispatch({
          type: ActionTypes.MENU_COMPLETED,
          payload: servicesWithTypes,
        });
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        errorApi.post(error as Error);
        dispatch({ type: ActionTypes.MENU_COMPLETED, payload: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [cloudResourcesMenuApi, errorApi, props.pageType, props.entityName]);

  // Rules of the current scope, for the per-resource failed checks column.
  // Account pages fetch their own rules, project pages need every rule to
  // join across their accounts, rule pages just the one rule.
  useEffect(() => {
    let cancelled = false;
    const complete = (payload: RuleEntity[]) => {
      if (!cancelled) {
        dispatch({ type: ActionTypes.RULES_COMPLETED, payload });
      }
    };

    if (props.pageType === PageType.RULE) {
      catalogApi
        .getEntities({
          filter: { kind: 'rule', 'metadata.name': props.entityName },
        })
        .then(value => complete(value.items as RuleEntity[]))
        .catch(error => {
          if (cancelled) {
            return;
          }
          errorApi.post(error as Error);
          complete([]);
        });
      return () => {
        cancelled = true;
      };
    }

    if (props.pageType === PageType.PROJECT) {
      catalogApi
        .getEntities({ filter: { kind: 'rule' } })
        .then(value => complete(value.items as RuleEntity[]))
        .catch(error => {
          if (cancelled) {
            return;
          }
          errorApi.post(error as Error);
          complete([]);
        });
      return () => {
        cancelled = true;
      };
    }

    catalogApi
      .getEntities({
        filter: { kind: 'rule', 'spec.account': props.entityName },
      })
      .then(value => complete(value.items as RuleEntity[]))
      .catch(error => {
        if (cancelled) {
          return;
        }
        errorApi.post(error as Error);
        complete([]);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, errorApi, props.pageType, props.entityName]);

  useEffect(() => {
    const filterResources = async () => {
      if (state.status !== 'filter') return;

      let filteredResources = state.allCloudResources;

      if (state.selectedCategories.length) {
        filteredResources = filteredResources.filter(resource =>
          state.selectedCategories.includes(resource.spec.category),
        );
      }

      if (state.selectedRegions.length) {
        filteredResources = filteredResources.filter(resource =>
          state.selectedRegions.includes(resource.spec.region),
        );
      }

      if (state.selectedAccounts.length) {
        filteredResources = filteredResources.filter(resource =>
          state.selectedAccounts.includes(resource.spec.account),
        );
      }

      if (state.selectedTypes.length) {
        filteredResources = filteredResources.filter(resource =>
          state.selectedTypes.includes(resource.spec.type),
        );
      }

      if (state.search) {
        filteredResources = filteredResources.filter(resource =>
          matchesSearch(resource, state.search),
        );
      }

      let resourceExample: CloudResourceEntity | undefined = undefined;
      if (state.selectedTypes.length === 1) {
        if (filteredResources.length > 0) {
          resourceExample = filteredResources[0];
        } else {
          resourceExample = state.allCloudResources.filter(resource =>
            state.selectedTypes.includes(resource.spec.type),
          )[0];
        }
      }

      dispatch({
        type: ActionTypes.FILTER_COMPLETED,
        payload: { filteredResources, resourceExample },
      });
    };

    filterResources();
  }, [
    state.status,
    state.allCloudResources,
    state.search,
    state.selectedTypes,
    state.selectedCategories,
    state.selectedRegions,
    state.selectedAccounts,
  ]);

  const failedRulesByResource = useMemo(() => {
    const map: Record<string, ResourceFailedRule[]> = {};
    for (const rule of state.rules) {
      for (const info of rule.spec?.resources ?? []) {
        if (!info.severity) continue;
        const entries = map[info.id] ?? (map[info.id] = []);
        if (!entries.some(entry => entry.ruleName === rule.metadata.name)) {
          entries.push({
            ruleName: rule.metadata.name,
            severity: info.severity,
          });
        }
      }
    }
    return map;
  }, [state.rules]);

  // The resources matching all the OTHER active filters (the dimension's own
  // filter is ignored), so pickers and category cards always show what each
  // option would yield right now.
  const facetResources = (dimension: FacetDimension): CloudResourceEntity[] => {
    let base = state.allCloudResources;

    if (state.search) {
      base = base.filter(resource => matchesSearch(resource, state.search));
    }
    if (dimension !== 'category' && state.selectedCategories.length) {
      base = base.filter(resource =>
        state.selectedCategories.includes(resource.spec.category),
      );
    }
    if (dimension !== 'region' && state.selectedRegions.length) {
      base = base.filter(resource =>
        state.selectedRegions.includes(resource.spec.region),
      );
    }
    if (dimension !== 'account' && state.selectedAccounts.length) {
      base = base.filter(resource =>
        state.selectedAccounts.includes(resource.spec.account),
      );
    }
    if (dimension !== 'type' && state.selectedTypes.length) {
      base = base.filter(resource =>
        state.selectedTypes.includes(resource.spec.type),
      );
    }
    return base;
  };

  const facetCounts = (dimension: FacetDimension): Record<string, number> => {
    const counts: Record<string, number> = {};
    const field = FACET_FIELD[dimension];
    for (const resource of facetResources(dimension)) {
      const value = resource.spec[field];
      if (!value) continue;
      counts[value] = (counts[value] ?? 0) + 1;
    }
    return counts;
  };

  const setSelectedGroupBy = (groupBy: string[]) =>
    dispatch({ type: ActionTypes.SELECT_GROUP_BY, payload: groupBy });
  const setSelectedRegions = (regions: string[]) =>
    dispatch({ type: ActionTypes.SELECT_REGIONS, payload: regions });
  const setSelectedAccounts = (accountsToSelect: string[]) =>
    dispatch({ type: ActionTypes.SELECT_ACCOUNTS, payload: accountsToSelect });
  const selectType = (type: string) =>
    dispatch({ type: ActionTypes.SELECT_TYPE, payload: type });
  const unselectType = (type: string) =>
    dispatch({ type: ActionTypes.UNSELECT_TYPE, payload: type });
  const selectService = (service: string) =>
    dispatch({ type: ActionTypes.SELECT_SERVICE, payload: service });
  const unselectService = (service: string) =>
    dispatch({ type: ActionTypes.UNSELECT_SERVICE, payload: service });
  const setSearch = (search: string) =>
    dispatch({ type: ActionTypes.SET_SEARCH, payload: search });
  const toggleCategory = (category: string) =>
    dispatch({ type: ActionTypes.TOGGLE_CATEGORY, payload: category });
  const setSelectedCategories = (categories: string[]) =>
    dispatch({ type: ActionTypes.SET_CATEGORIES, payload: categories });
  const selectResource = (resource: CloudResourceEntity) =>
    dispatch({
      type: ActionTypes.SELECT_RESOURCE,
      payload: resource,
    });
  const unselectResource = () =>
    dispatch({ type: ActionTypes.UNSELECT_RESOURCE });

  const context = useMemo(
    () => ({
      cloudResources: state.cloudResources,
      resourceExample: state.resourceExample,
      selectedResource: state.selectedResource,
      allGroupBy: state.allGroupBy,
      selectedGroupBy: state.selectedGroupBy,
      allRegions: state.allRegions,
      selectedRegions: state.selectedRegions,
      allAccounts: state.allAccounts,
      selectedAccounts: state.selectedAccounts,
      accountProvider: Object.fromEntries(
        (accounts as AccountEntity[]).map(account => [
          account.metadata.name,
          account.spec.type ?? '',
        ]),
      ) as Record<string, string>,
      allCategoriesWithServices: state.allCategoriesWithServices,
      allServicesWithTypes: state.allServicesWithTypes,
      menuServicesWithTypes: state.menuServicesWithTypes,
      selectedTypes: state.selectedTypes,
      selectedCategories: state.selectedCategories,
      search: state.search,
      rules: state.rules,
      failedRulesByResource,
      hasRegionFilter: state.hasRegionFilter,
      hasTypesFilter: state.hasTypesFilter,
      hasAccountFilter: state.hasAccountFilter,
      setSelectedGroupBy,
      setSelectedRegions,
      setSelectedAccounts,
      selectType,
      unselectType,
      selectService,
      unselectService,
      setSearch,
      toggleCategory,
      setSelectedCategories,
      facetResources,
      facetCounts,
      selectResource,
      unselectResource,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.cloudResources,
      state.resourceExample,
      state.selectedResource,
      state.allGroupBy,
      state.selectedGroupBy,
      state.allRegions,
      state.selectedRegions,
      state.allAccounts,
      state.selectedAccounts,
      accounts,
      state.allCategoriesWithServices,
      state.allServicesWithTypes,
      state.menuServicesWithTypes,
      state.selectedTypes,
      state.selectedCategories,
      state.search,
      state.rules,
      state.hasRegionFilter,
      state.hasTypesFilter,
      state.hasAccountFilter,
      failedRulesByResource,
    ],
  );

  return (
    <CloudResourcesContext.Provider value={context}>
      {props.children}
    </CloudResourcesContext.Provider>
  );
}
